import { TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { TabConfig } from '@/lib/config/tabs'

interface TabGroupProps {
  tabs: TabConfig[]
  badges?: Record<string, number | string>
  className?: string
  value?: string
  onValueChange?: (value: string) => void
}

export function TabGroup({ tabs, badges, className, value, onValueChange }: TabGroupProps) {
  // Map tab count to Tailwind grid classes
  const gridColsMap: Record<number, string> = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5',
    6: 'grid-cols-6',
  }

  const gridCols = gridColsMap[tabs.length] || 'grid-cols-4'

  // Get the label for the currently selected tab
  const getCurrentLabel = () => {
    const currentTab = tabs.find((tab) => tab.value === value)
    if (!currentTab) return tabs[0]?.label || ''

    const Icon = currentTab.icon
    const hasBadge = badges && badges[currentTab.value] !== undefined

    return (
      <span className="flex items-center gap-2">
        <Icon className="h-4 w-4" />
        {currentTab.label}
        {hasBadge && (
          <Badge variant="secondary" className="ml-1 text-xs">
            {badges[currentTab.value]}
          </Badge>
        )}
      </span>
    )
  }

  const handleSelectChange = (newValue: string) => {
    if (onValueChange) {
      onValueChange(newValue)
    }
  }

  return (
    <>
      {/* Mobile Select Dropdown (shown on small screens) */}
      <div className="md:hidden w-full mb-3">
        <Select value={value} onValueChange={handleSelectChange}>
          <SelectTrigger className="w-full">
            <SelectValue>{getCurrentLabel()}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {tabs.map((tab) => {
              const Icon = tab.icon
              const hasBadge = badges && badges[tab.value] !== undefined

              return (
                <SelectItem key={tab.value} value={tab.value}>
                  <span className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    {tab.label}
                    {hasBadge && (
                      <Badge variant="secondary" className="ml-2 text-xs">
                        {badges[tab.value]}
                      </Badge>
                    )}
                  </span>
                </SelectItem>
              )
            })}
          </SelectContent>
        </Select>
      </div>

      {/* Desktop Tabs (shown on medium screens and up) */}
      <TabsList className={`hidden md:grid w-full ${gridCols} ${className || ''}`}>
        {tabs.map((tab) => {
          const Icon = tab.icon
          const hasBadge = badges && badges[tab.value] !== undefined

          return (
            <TabsTrigger key={tab.value} value={tab.value} className="gap-2">
              <Icon className="h-4 w-4" />
              {tab.label}
              {hasBadge && (
                <Badge variant="secondary" className="ml-1">
                  {badges[tab.value]}
                </Badge>
              )}
            </TabsTrigger>
          )
        })}
      </TabsList>
    </>
  )
}
